import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {TextInput, SelectInput, Text, Button, StatusBadge} from '@/ui-kits';
import {SelectInputitem, DataTableColumn, SupportTicket} from '@/interface';
import {useToast} from '@/hooks';
import {getSupportTicketsRequest, resetSupportAdminMessage} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {
  supportTicketsDataSelector,
  supportAdminSuccessSelector,
  supportAdminFailureSelector,
  supportTicketsTotalPagesSelector,
  supportTicketsTotalResultsSelector,
  supportTicketsCurrentPageSelector,
  supportAdminStatus,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, SuccessCodes} from '@/utils/getMessages';
import {HelpRequestStatus, HelpRequestUserType, HelpRequestUserTypeLabels} from '@/constants';
import {formatDate} from '@/utils/common_functions';
import {HelpRequestStatusBadgeVariants, HelpRequestStatusLabels, NA} from '@/constants/defaults';
import {OpenStatusModal, ClosedStatusModal, SpamStatusModal, DataTable} from '@/components';

const statusOptions: SelectInputitem[] = [
  {id: HelpRequestStatus.Open, label: 'Open'},
  {id: HelpRequestStatus.Closed, label: 'Closed'},
  {id: HelpRequestStatus.Spam, label: 'Spam'},
];

const userTypeOptions: SelectInputitem[] = [
  {id: HelpRequestUserType.Patient, label: 'Patient'},
  {id: HelpRequestUserType.Specialist, label: 'Specialist'},
  {id: HelpRequestUserType.Visitor, label: 'Visitor'},
  {id: HelpRequestUserType.HospitalPoc, label: 'Hospital POC'},
];

export const SupportTickets: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==========================
  // selectors
  // ==========================
  const tickets = useSelector(supportTicketsDataSelector);
  const success = useSelector(supportAdminSuccessSelector) as SuccessCodes;
  const failure = useSelector(supportAdminFailureSelector) as ErrorCodes;
  const totalPages = useSelector(supportTicketsTotalPagesSelector);
  const totalResults = useSelector(supportTicketsTotalResultsSelector);
  const currentPage = useSelector(supportTicketsCurrentPageSelector);
  const loading = useSelector(supportAdminStatus);

  // ==========================
  // states
  // ==========================
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<SelectInputitem | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<SelectInputitem | null>(null);
  const [page, setPage] = useState(1);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to get user type label from enum
  const getUserTypeLabel = useCallback((userType: number | null | undefined): string => {
    if (userType === null || userType === undefined) {
      return NA;
    }

    if (!HelpRequestUserTypeLabels[userType as HelpRequestUserType]) {
      return NA;
    }

    return HelpRequestUserTypeLabels[userType as HelpRequestUserType];
  }, []);

  // ==========================
  // event handlers & functions
  // ==========================
  const handleViewTicket = useCallback((ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  }, []);

  // side effect to fetch tickets
  const fetchTickets = useCallback(() => {
    let isFilterActive = false;
    const payload: any = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterActive = true;
    }
    if (selectedStatus) {
      payload.status = selectedStatus.id;
      isFilterActive = true;
    }
    if (selectedUserType) {
      payload.user_type = selectedUserType.id;
      isFilterActive = true;
    }
    setIsFilterActive(isFilterActive);
    dispatch(getSupportTicketsRequest(payload));
  }, [search, selectedStatus, selectedUserType, page]);

  const handleModalSuccess = useCallback(() => {
    // Refresh tickets after successful reply or spam action
    handleCloseModal();
    // Use setTimeout to ensure modal closes before refreshing
    setTimeout(() => {
      fetchTickets();
    }, 100);
  }, [handleCloseModal, fetchTickets]);

  // ==========================
  // columns
  // ==========================
  const columns = useMemo((): DataTableColumn<SupportTicket>[] => {
    return [
      {
        name: 'id',
        title: <Text>QUERY ID</Text>,
        render: row => String(row.id),
        headerAlign: 'left',
        width: {minWidth: '120px', maxWidth: '150px'},
        align: 'left',
      },
      {
        name: 'email',
        title: <Text>EMAIL</Text>,
        render: row => <div className="whitespace-normal break-words">{row?.email ?? NA}</div>,
        headerAlign: 'left',
        width: {minWidth: '150px', maxWidth: '250px'},
        align: 'left',
      },
      {
        name: 'user_type',
        title: <Text>USER TYPE</Text>,
        render: row => getUserTypeLabel(row?.user_type),
        headerAlign: 'center',
        width: {minWidth: '120px', maxWidth: '150px'},
        align: 'center',
      },
      {
        name: 'status',
        title: <Text>STATUS</Text>,
        width: {minWidth: '120px', maxWidth: '150px'},
        render: row => (
          <StatusBadge variant="secondary" status={HelpRequestStatusBadgeVariants[row?.status as HelpRequestStatus]}>
            {HelpRequestStatusLabels[row?.status as HelpRequestStatus]}
          </StatusBadge>
        ),
        align: 'center',
        headerAlign: 'center',
      },
      {
        name: 'created_at',
        title: <Text>CREATED DATE</Text>,
        render: row => (row?.created_at ? formatDate(row.created_at, 'yyyy-MMM-dd, hh:mm:ss') : NA),
        headerAlign: 'center',
        width: {minWidth: '200px', maxWidth: '250px'},
        align: 'center',
      },
      {
        name: 'actions',
        title: <Text>ACTIONS</Text>,
        render: row => (
          <button onClick={() => handleViewTicket(row)} className="text-upload_text_blue font-FigtreeSemiBold text-base">
            View
          </button>
        ),
        headerAlign: 'center',
        width: {minWidth: '100px', maxWidth: '120px'},
        align: 'center',
      },
    ];
  }, [getUserTypeLabel, handleViewTicket]);

  const resetFilter = useCallback(() => {
    setPage(1);
    setSearch('');
    setSelectedStatus(null);
    setSelectedUserType(null);
    setIsFilterActive(false);
  }, []);

  // ==========================
  // side effects
  // ==========================
  useEffect(() => {
    const timeout = setTimeout(fetchTickets, 500);
    return () => clearTimeout(timeout);
  }, [fetchTickets]);

  useEffect(() => {
    if (failure && !['E-10044', 'E-10054'].includes(failure)) {
      showToast(getErrorMessage(failure), 'failure');
    }
    if (success && success === 'S-10093') {
      showToast('Action completed successfully', 'success');
    }

    return () => {
      dispatch(resetSupportAdminMessage());
    };
  }, [failure, success, dispatch, showToast]);

  const resetButton = useMemo(() => {
    return isFilterActive && <Button title="Reset Filters" className="w-fit" variant="secondary" onClick={resetFilter} />;
  }, [isFilterActive, resetFilter]);

  return (
    <div className="flex flex-col grow p-2 sm:p-4 md:p-6 lg:p-8">
      <Text variant="body" className="!font-FigtreeMeduim text-deep_blue !text-xl sm:!text-2xl">
        Manage and respond to support requests
      </Text>

      <div className="flex flex-col sm:flex-row sm:flex-nowrap items-center gap-4 my-4">
        <TextInput
          value={search}
          onChangeText={setSearch}
          wrapperClassName="border pl-2"
          className="w-full sm:flex-1 sm:min-w-[250px] sm:max-w-[300px]"
          leftIcon="search_blue"
          name="search"
          placeholder="Search by Query ID & Email."
        />
        <div className="flex flex-row gap-4 w-full sm:contents">
          <SelectInput
            wrapperClassName="border"
            name="status"
            className="w-full sm:flex-1 sm:min-w-[80px] sm:max-w-[140px]"
            placeholder="Status"
            options={statusOptions}
            value={selectedStatus?.id || ''}
            onChange={setSelectedStatus}
          />
          <SelectInput
            wrapperClassName="border"
            name="user_type"
            className="w-full sm:flex-1 sm:min-w-[100px] sm:max-w-[140px]"
            placeholder="User Type"
            options={userTypeOptions}
            value={selectedUserType?.id || ''}
            onChange={setSelectedUserType}
          />
        </div>
        <div className="hidden md:flex ml-0 flex-shrink-0">{resetButton}</div>
      </div>
      <div className="md:hidden mb-4">{resetButton}</div>

      <DataTable
        data={tickets}
        columns={columns}
        totalPages={totalPages}
        totalResult={totalResults}
        currentPage={currentPage}
        onPageChange={setPage}
        loading={loading}      
        />

      {selectedTicket && (
        <>
          {selectedTicket.status === HelpRequestStatus.Open && (
            <OpenStatusModal open={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} onSuccess={handleModalSuccess} />
          )}
          {selectedTicket.status === HelpRequestStatus.Closed && <ClosedStatusModal open={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} />}
          {selectedTicket.status === HelpRequestStatus.Spam && <SpamStatusModal open={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} />}
        </>
      )}
    </div>
  );
};
